import React from 'react';
import { Container, Row, Col, Card, Button, Alert, Badge } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
//import { useSelector } from 'react-redux';
import { useGetMyCoursesQuery } from '../slices/ordersApiSlice';
import { useGetPlaylistsByProductQuery } from '../slices/playlistsApiSlice';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { getImageUrl } from '../utils/imageUtils';

const CoursePlaylistsScreen = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  //const { userInfo } = useSelector((state) => state.auth);
  
  // Vérifier que l'utilisateur a bien acheté ce cours
  const { data: coursesData, isLoading: coursesLoading } = useGetMyCoursesQuery();
  const { data: playlistsData, isLoading: playlistsLoading, error } = useGetPlaylistsByProductQuery(productId);

  const isLoading = coursesLoading || playlistsLoading;

  // Vérifier l'accès
  const hasAccess = coursesData?.courses?.some(
    course => course.productId === productId
  );

  if (isLoading) return <Loader />;

  // Rediriger si pas d'accès
  if (!hasAccess && !isLoading) {
    return (
      <Container>
        <Alert variant="warning" className="text-center py-5">
          <h4>Accès non autorisé</h4>
          <p className="mb-3">
            Vous devez acheter cette formation pour accéder à son contenu.
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <Button variant="primary" onClick={() => navigate(`/product/online/${productId}`)}>
              Acheter cette formation
            </Button>
            <Button variant="outline-secondary" onClick={() => navigate('/my-courses')}>
              Retour à mes formations
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  const course = coursesData?.courses.find(c => c.productId === productId);

  return (
    <Container>
      {/* En-tête */}
      <div className="mb-4">
        <Button 
          variant="outline-secondary" 
          onClick={() => navigate('/my-courses')}
          className="mb-3"
        >
          <i className="fas fa-arrow-left me-2"></i>
          Retour à mes formations
        </Button>
        
        {course && (
          <div className="d-flex align-items-center mb-3">
            <img 
              src={getImageUrl(course.product.image)} 
              alt={course.product.name}
              style={{ 
                width: '80px', 
                height: '60px', 
                objectFit: 'cover',
                borderRadius: '8px',
                marginRight: '15px'
              }}
              onError={(e) => {
                e.target.src = '/images/placeholder.jpg';
              }}
            />
            <div>
              <h1 className="h3 mb-1">{course.product.name}</h1>
              <p className="text-muted mb-0">{course.product.description}</p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <Message variant="danger">
          {error?.data?.message || error.error || 'Erreur lors du chargement du contenu'}
        </Message>
      )}

      {/* Liste des playlists */}
      {playlistsData?.playlists && playlistsData.playlists.length === 0 ? (
        <Alert variant="info" className="text-center py-5">
          <div className="mb-3">
            <i className="fas fa-film fa-3x text-muted"></i>
          </div>
          <h4>Contenu en préparation</h4>
          <p className="mb-0">
            Le contenu vidéo de cette formation est en cours de préparation et sera disponible prochainement.
          </p>
        </Alert>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Contenu de la formation</h2>
            <Badge bg="primary" className="fs-6">
              {playlistsData?.playlists?.length || 0} playlist(s)
            </Badge>
          </div>

          <Row>
            {playlistsData?.playlists?.map((playlist) => (
              <Col key={playlist._id} lg={6} className="mb-4">
                <Card className="h-100 shadow-sm">
                  <Card.Img
                    variant="top"
                    src={getImageUrl(playlist.image)}
                    alt={playlist.name}
                    style={{ 
                      height: '200px', 
                      objectFit: 'cover' 
                    }}
                    onError={(e) => {
                      e.target.src = '/images/placeholder-playlist.jpg';
                    }}
                  />
                  
                  <Card.Body className="d-flex flex-column">
                    <Card.Title className="h5">
                      {playlist.name}
                    </Card.Title>
                    
                    <Card.Text className="text-muted flex-grow-1">
                      {playlist.description || 'Aucune description disponible'}
                    </Card.Text>

                    <div className="mt-auto">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">
                          <i className="fas fa-video me-1"></i>
                          {playlist.videos?.length || 0} vidéo(s)
                        </small>
                        <Badge bg="outline-primary">
                          {Math.round((playlist.videos?.length || 0) * 10)} min
                        </Badge>
                      </div>
                      
                      <Link to={`/playlist/${playlist._id}`}>
                        <Button variant="primary" className="w-100">
                          <i className="fas fa-play me-2"></i>
                          Commencer à apprendre
                        </Button>
                      </Link>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}
    </Container>
  );
};

export default CoursePlaylistsScreen;